import React from 'react';
import PropTypes from 'prop-types';
import * as ReactRedux from 'react-redux';
import { bindActionCreators } from 'redux';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import classNames from 'classnames';
import CustomPropTypes from 'CustomPropTypes';
import { SelfServeBuilderPopupTypes } from 'Popups';
import * as SectionOrganizerActions from './SectionOrganizerActions';
import SectionDivider from './SectionDivider';
import TextBoxSectionItem from './TextBoxSectionItem';
import SignatureSectionItem from './SignatureSectionItem';
import GroupedChoiceSectionItem from './GroupedChoiceSectionItem';

const { CHECKBOX, MULTIPLE_CHOICE, SIGNATURE, TEXTBOX } = SelfServeBuilderPopupTypes;
export class Section extends React.PureComponent {
  constructor(props) {
    super(props);

    this.getSection = this.getSection.bind(this);
    this.getSectionItem = this.getSectionItem.bind(this);
    this.getSectionSample = this.getSectionSample.bind(this);
    this.getSectionContent = this.getSectionContent.bind(this);
  }

  getSectionSample() {
    if (this.props.showSample) {
      return (
        <TextBoxSectionItem
          fieldData={{
            label: 'Sample Textbox',
          }}
          fieldId={'sample-textbox'}
          index={0}
          isSample
        />
      );
    }

    return null;
  }

  getSection(fieldId, index) {
    if (this.props.popups[fieldId]) {
      return this.getSectionItem(fieldId, index);
    } else if (this.props.popupGroups[fieldId]) {
      return this.getSectionItemGroup(fieldId, index);
    }
    return null;
  }

  getSectionItem(fieldId, index) {
    switch (this.props.popups[fieldId].type) {
      case TEXTBOX:
        return (
          <TextBoxSectionItem
            fieldData={this.props.popups[fieldId]}
            fieldId={fieldId}
            index={index}
            popups={this.props.popups}
          />
        );
      case SIGNATURE:
        return (
          <SignatureSectionItem
            fieldData={this.props.popups[fieldId]}
            fieldId={fieldId}
            index={index}
            popups={this.props.popups}
          />
        );
      default:
        return null;
    }
  }

  getSectionItemGroup(fieldId, index) {
    const group = this.props.popupGroups[fieldId];
    const groupedPopups = {};
    group.fieldIds.forEach(
      (key) => {
        groupedPopups[key] = this.props.popups[key];
      },
    );
    switch (this.props.popupGroups[fieldId].type) {
      case CHECKBOX:
      case MULTIPLE_CHOICE:
        return (
          <GroupedChoiceSectionItem
            fieldId={fieldId}
            group={group}
            groupedPopups={groupedPopups}
            index={index}
            popupGroups={this.props.popupGroups}
          />
        );
      default:
        return null;
    }
  }

  getSectionContent() {
    const sectionContent = (
      <div className="organization-section">

        <SectionDivider
          isDone
          isEditable={this.props.section.isNameEditable}
          isMultiField={this.props.section.isMultiField}
          name={this.props.section.name}
          sectionIndex={this.props.sectionIndex}
          isOnlySection={this.props.sectionCount === 1}
        />

        {this.getSectionSample()}

        {this.props.section.fieldIds.map((fieldId, index) => {
          const includeAddSection = index > 0;
          const isLastFieldInOrganizer = (
            this.props.sectionCount - 1 === this.props.sectionIndex
            && index === this.props.section.fieldIds.length - 1
          );
          const lastFieldClasses = classNames(
            'organization-section-items-container',
            { 'organization-section-items-container-last': isLastFieldInOrganizer },
          );

          return (
            <React.Fragment key={fieldId}>
              {includeAddSection &&
              <SectionDivider
                isEditable={this.props.section.isNameEditable}
                fieldIndex={index}
                key={`${fieldId}-section-divider`}
                sectionIndex={this.props.sectionIndex}
              />
              }
              <div
                className={lastFieldClasses}
              >
                {this.getSection(fieldId, index)}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
    return sectionContent;
  }

  render() {
    const self = this;
    return (
      <Draggable
        draggableId={self.props.section.id}
        index={this.props.sectionIndex}
        isDragDisabled={!this.props.section.isDraggable}
      >
        {provided => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={provided.draggableProps.style}
          >
            <Droppable
              droppableId={self.props.section.id}
              isDropDisabled={!this.props.section.isDroppable}
            >
              {providedDrop => (
                <div
                  ref={providedDrop.innerRef}
                  {...providedDrop.droppableProps}
                >
                  {self.getSectionContent()}
                  {providedDrop.placeholder}
                </div>
              )}
            </Droppable>
            {provided.placeholder}
          </div>
        )}
      </Draggable>
    );
  }
}

Section.defaultProps = {
  popups: {},
  showSample: false,
};

Section.propTypes = {
  popups: CustomPropTypes.hashmapOf(
    PropTypes.shape({
      label: PropTypes.string,
      type: PropTypes.string,
    }),
  ),
  section: PropTypes.shape({
    fieldIds: PropTypes.arrayOf(PropTypes.string),
    id: PropTypes.string,
    isDraggable: PropTypes.bool,
    isDroppable: PropTypes.bool,
    isMultiField: PropTypes.bool,
    isNameEditable: PropTypes.bool,
    name: PropTypes.string,
  }).isRequired,
  sectionCount: PropTypes.number.isRequired,
  sectionIndex: PropTypes.number.isRequired,
  showSample: PropTypes.bool,
  popupGroups: CustomPropTypes.hashmapOf(
    PropTypes.shape({
      label: PropTypes.string,
      type: PropTypes.string,
      pageIndex: PropTypes.number,
      fieldIds: PropTypes.arrayOf(PropTypes.string),
    }),
  ).isRequired,
};


const mapStateToProps = ({
  popupHandler,
  SelfServePostBuilderSidePanelHandler,
  PopupGroupReducer,
}, ownProps) => ({
  section: SelfServePostBuilderSidePanelHandler.SectionOrganizerReducer.sections[
    ownProps.sectionIndex
  ],
  sectionCount: SelfServePostBuilderSidePanelHandler.SectionOrganizerReducer.sections.length,
  popups: popupHandler.popups,
  popupGroups: PopupGroupReducer.popupGroups,
});

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    ...SectionOrganizerActions,
  }, dispatch)
);

export default ReactRedux.connect(
  mapStateToProps,
  mapDispatchToProps,
)(Section);



// WEBPACK FOOTER //
// ./src-react/SelfServePostBuilderSidePanel/SectionOrganizer/Section.jsx